import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Assignment, Student, GradeEntry, QualificationGrade } from '../types';

interface BulkGradeEntryProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: Assignment;
  students: Student[];
  onBulkSave: (grades: Map<string, Partial<GradeEntry>>) => void;
}

const BulkGradeEntry = ({
  isOpen,
  onClose,
  assignment,
  students,
  onBulkSave,
}: BulkGradeEntryProps) => {
  const [grades, setGrades] = useState<Map<string, string>>(new Map());

  if (!isOpen) return null;

  const isPrimaryLevel = students[0]?.educationLevel === 'primary';

  const qualificationOptions = [
    { value: 'FB', label: 'FB - Foarte Bine' },
    { value: 'B', label: 'B - Bine' },
    { value: 'S', label: 'S - Suficient' },
    { value: 'I', label: 'I - Insuficient' },
  ];

  const handleGradeChange = (studentId: string, value: string) => {
    const newGrades = new Map(grades);
    newGrades.set(studentId, value);
    setGrades(newGrades);
  };

  const handleSave = () => {
    const gradeEntries = new Map<string, Partial<GradeEntry>>();

    grades.forEach((value, studentId) => {
      if (value) {
        if (isPrimaryLevel) {
          gradeEntries.set(studentId, {
            qualification: value as QualificationGrade,
            lastModified: new Date().toISOString(),
            modifiedBy: 'Profesor curent',
          });
        } else {
          const numericValue = parseFloat(value);
          if (!isNaN(numericValue)) {
            gradeEntries.set(studentId, {
              score: numericValue,
              lastModified: new Date().toISOString(),
              modifiedBy: 'Profesor curent',
            });
          }
        }
      }
    });

    onBulkSave(gradeEntries);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[1100] animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[1110] flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-scale-in">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Introducere note în masă</h2>
              <p className="text-sm text-muted-foreground mt-1">{assignment.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
              aria-label="Închide fereastra"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {students.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center gap-4 p-4 bg-muted rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{student.name}</p>
                    <p className="text-xs text-muted-foreground">{student.studentId}</p>
                  </div>
                  <div className="w-48">
                    {isPrimaryLevel ? (
                      <Select
                        options={qualificationOptions}
                        value={grades.get(student.id) || ''}
                        onChange={(value) => handleGradeChange(student.id, value.toString())}
                        placeholder="Selectează nota"
                      />
                    ) : (
                      <Input
                        type="number"
                        value={grades.get(student.id) || ''}
                        onChange={(e) => handleGradeChange(student.id, e.target.value)}
                        placeholder="0"
                        min={0}
                        max={assignment.maxScore}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button variant="default" onClick={handleSave} iconName="Save">
              Salvează toate notele
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkGradeEntry;
